/**
 * FERP Portal - Workflow State View
 *
 * Engine-driven task state visualization.
 * Columns reflect actual Task FSM states — not user-arranged sticky notes.
 * Actions go through engine validation (startTask, completeTask, etc.).
 *
 * FERP Principles:
 * - Dumb Frontend: Board layout from config, task data from backend
 * - NO drag-and-drop status changes: Engine validates all transitions
 * - NO hardcoded values: Status/priority from generated enums
 * - Repository pattern: All mutations through generated Apollo hooks
 */

import { useState, useCallback } from 'react';
import {
  Container, Group, Stack, Text, Title, Card, Badge, Button, Alert,
  Loader, ScrollArea, ThemeIcon, Tooltip, TextInput, Paper, Switch,
} from '@mantine/core';
import {
  IconLayoutColumns, IconRefresh, IconAlertTriangle, IconSearch,
  IconActivity, IconCheckbox, IconAlertOctagon, IconClock,
  IconChevronRight, IconChevronDown, IconPlayerPlay,
  IconUserCheck, IconLoader, IconPlayerPause, IconExternalLink,
  IconCheck, IconX, IconClockHour4, IconEye, IconTimeline, IconCalendar,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  GqlTaskStatus,
  useStartTaskMutation,
  useCompleteTaskMutation,
  type TaskFieldsFragment,
} from '@/generated/graphql';
import { useWorkflowStateView, type BoardColumn } from '@/hooks/useWorkflowStateView';
import { TaskCard } from './TaskCard';
import { TaskDetailPanel } from './TaskDetailPanel';
import { DEMO_TASKS } from './demoTasks';

/** Column icon mapping */
const COLUMN_ICONS: Record<string, React.ReactNode> = {
  [GqlTaskStatus.PENDING]: <IconClockHour4 size={14} />,
  [GqlTaskStatus.READY]: <IconPlayerPlay size={14} />,
  [GqlTaskStatus.ASSIGNED]: <IconUserCheck size={14} />,
  [GqlTaskStatus.IN_PROGRESS]: <IconLoader size={14} />,
  [GqlTaskStatus.BLOCKED]: <IconAlertTriangle size={14} />,
  [GqlTaskStatus.PAUSED]: <IconPlayerPause size={14} />,
  [GqlTaskStatus.PENDING_EXTERNAL]: <IconExternalLink size={14} />,
  [GqlTaskStatus.COMPLETED]: <IconCheck size={14} />,
  [GqlTaskStatus.FAILED]: <IconX size={14} />,
};

/** Stat card in the summary header */
function StatCard({ label, value, color, icon }: {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Card withBorder p="xs" style={{ flex: 1, minWidth: 100 }}>
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon variant="light" size="sm" color={color}>{icon}</ThemeIcon>
        <div>
          <Text size="xs" c="dimmed">{label}</Text>
          <Text size="lg" fw={700}>{value}</Text>
        </div>
      </Group>
    </Card>
  );
}

/** Single status column */
function StatusColumn({ column, searchQuery, onAction, onCardClick }: {
  column: BoardColumn;
  searchQuery: string;
  onAction: (taskId: string, workflowInstanceId: string, action: string) => void;
  onCardClick: (task: TaskFieldsFragment) => void;
}) {
  const [expanded, setExpanded] = useState(!column.collapsed);

  const filteredTasks = searchQuery
    ? column.tasks.filter(t =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : column.tasks;

  const count = filteredTasks.length;
  const isOverWip = column.wipLimit != null && count > column.wipLimit;
  const columnIcon = COLUMN_ICONS[column.status];

  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      style={{
        width: 260,
        minWidth: 260,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 300px)',
        minHeight: 300,
        backgroundColor: 'var(--mantine-color-dark-7)',
      }}
    >
      {/* Column header */}
      <Group
        justify="space-between"
        mb="xs"
        pb="xs"
        style={{
          cursor: column.collapsed ? 'pointer' : 'default',
          borderBottom: '1px solid var(--mantine-color-dark-4)',
          flexShrink: 0,
        }}
        onClick={column.collapsed ? () => setExpanded(!expanded) : undefined}
      >
        <Group gap={6} wrap="nowrap">
          {column.collapsed && (
            expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />
          )}
          <ThemeIcon variant="light" size="sm" color={column.color} radius="sm">
            {columnIcon}
          </ThemeIcon>
          <Text size="sm" fw={600}>{column.label}</Text>
          <Badge size="xs" variant="filled" color={column.color} circle>
            {count}
          </Badge>
        </Group>
        {column.wipLimit != null && (
          <Tooltip label={`WIP limit: ${column.wipLimit}`}>
            <Badge
              size="xs"
              variant={isOverWip ? 'filled' : 'outline'}
              color={isOverWip ? 'red' : 'dark'}
            >
              {count}/{column.wipLimit}
            </Badge>
          </Tooltip>
        )}
      </Group>

      {/* WIP warning */}
      {isOverWip && expanded && (
        <Alert color="red" p={4} mb="xs" styles={{ message: { fontSize: 11 } }} style={{ flexShrink: 0 }}>
          WIP limit exceeded ({count}/{column.wipLimit})
        </Alert>
      )}

      {/* Task cards */}
      {expanded && (
        <ScrollArea style={{ flex: 1 }} offsetScrollbars scrollbarSize={6}>
          <Stack gap={8}>
            {filteredTasks.length === 0 ? (
              <Stack gap="xs" align="center" py="xl">
                <ThemeIcon variant="light" size="lg" color={column.color} radius="xl">
                  {columnIcon}
                </ThemeIcon>
                <Text size="xs" c="dimmed">No tasks</Text>
              </Stack>
            ) : (
              filteredTasks.map((task) => (
                <TaskCard key={task.id} task={task} onAction={onAction} onClick={onCardClick} />
              ))
            )}
          </Stack>
        </ScrollArea>
      )}

      {/* Collapsed summary */}
      {!expanded && count > 0 && (
        <Text size="xs" c="dimmed" ta="center">
          {count} task{count !== 1 ? 's' : ''}
        </Text>
      )}
    </Paper>
  );
}

export function WorkflowStateView() {
  const navigate = useNavigate();
  const [demoMode, setDemoMode] = useState(false);
  const { columns, stats, loading, error, refetch } = useWorkflowStateView(demoMode ? DEMO_TASKS : undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [startTask] = useStartTaskMutation();
  const [completeTask] = useCompleteTaskMutation();
  const [selectedTask, setSelectedTask] = useState<TaskFieldsFragment | null>(null);
  const [panelOpened, setPanelOpened] = useState(false);

  const totalTasks = columns.reduce((sum, c) => sum + c.tasks.length, 0);

  const handleAction = useCallback(async (taskId: string, workflowInstanceId: string, action: string) => {
    if (demoMode) return;
    try {
      switch (action) {
        case 'start':
        case 'resume':
          await startTask({ variables: { workflowInstanceId, taskId } });
          break;
        case 'complete':
          await completeTask({
            variables: { workflowInstanceId, input: { taskId, outputData: {} } },
          });
          break;
      }
      refetch();
    } catch (err) {
      console.error('Task action failed:', err);
    }
  }, [demoMode, startTask, completeTask, refetch]);

  if (error && !demoMode) {
    return (
      <Container fluid py="md" px="md">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Board">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid py="md" px="md">
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <ThemeIcon variant="light" size="lg" color="cyan">
              <IconLayoutColumns size={20} />
            </ThemeIcon>
            <div>
              <Title order={3}>Workflow State View</Title>
              <Text size="xs" c="dimmed">
                Engine-driven task state visualization
                {totalTasks > 0 && ` \u2022 ${totalTasks} tasks`}
                {demoMode && ' \u2022 Demo Mode'}
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button
              leftSection={<IconTimeline size={14} />}
              variant="subtle"
              size="xs"
              onClick={() => navigate('/ferp-core/gantt')}
            >
              Gantt View
            </Button>
            <Button
              leftSection={<IconCalendar size={14} />}
              variant="subtle"
              size="xs"
              onClick={() => navigate('/ferp-core/calendar')}
            >
              Calendar
            </Button>
            <Switch
              label="Demo"
              size="xs"
              checked={demoMode}
              onChange={(e) => setDemoMode(e.currentTarget.checked)}
              thumbIcon={demoMode ? <IconEye size={10} /> : undefined}
            />
            <Button
              leftSection={<IconRefresh size={14} />}
              variant="light"
              onClick={() => refetch()}
              loading={loading}
              size="xs"
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Stats row */}
        <Group gap="xs" wrap="wrap">
          <StatCard label="Active" value={stats.active} color="cyan" icon={<IconActivity size={14} />} />
          <StatCard label="Blocked" value={stats.blocked} color="orange" icon={<IconAlertOctagon size={14} />} />
          <StatCard label="Completed" value={stats.completed} color="green" icon={<IconCheckbox size={14} />} />
          <StatCard label="Failed" value={stats.failed} color="red" icon={<IconAlertTriangle size={14} />} />
          {stats.slaApproaching > 0 && (
            <StatCard label="SLA Risk" value={stats.slaApproaching} color="yellow" icon={<IconClock size={14} />} />
          )}
        </Group>

        {/* Search */}
        <TextInput
          placeholder="Filter tasks..."
          leftSection={<IconSearch size={14} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          size="xs"
        />

        {/* Board columns — horizontal scroll, full width */}
        {loading && totalTasks === 0 ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : (
          <ScrollArea type="auto" offsetScrollbars scrollbarSize={8}>
            <Group gap="sm" wrap="nowrap" align="stretch" style={{ paddingBottom: 4 }}>
              {columns.map((column) => (
                <StatusColumn
                  key={column.status}
                  column={column}
                  searchQuery={searchQuery}
                  onAction={handleAction}
                  onCardClick={(task) => { setSelectedTask(task); setPanelOpened(true); }}
                />
              ))}
            </Group>
          </ScrollArea>
        )}
      </Stack>

      <TaskDetailPanel
        selectedTask={selectedTask}
        opened={panelOpened}
        onClose={() => setPanelOpened(false)}
        allTasks={columns.flatMap(c => c.tasks)}
        onRefetch={refetch}
        demoMode={demoMode}
        onTaskSelect={(task) => setSelectedTask(task)}
      />
    </Container>
  );
}

export default WorkflowStateView;
